import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { HOME_CONTENT_MOCK } from '../../data/home-content.mock';
import type { HomeTestimonioItem } from '../../models/home-testimonios-content.model';
import { HomeTestimoniosService } from '../../services/home-testimonios.service';
import { CookieConsentService } from '../../../../shared/cookie-consent/cookie-consent.service';

const YOUTUBE_PLACEHOLDER_THUMB =
  'assets/img/Home/testimonios/testimonio-luz.png';

@Component({
  selector: 'app-home-testimonios',
  templateUrl: './home-testimonios.component.html',
  styleUrls: ['./home-testimonios.component.scss'],
  standalone: false,
})
export class HomeTestimoniosComponent implements OnInit, OnDestroy {
  eyebrow: string = String(HOME_CONTENT_MOCK.testimonios.eyebrow);
  titleLead = 'Testimonios de';
  titleAccent = 'emprendedores como tú';

  testimonials: HomeTestimonioItem[] = [];
  currentIndex = 0;
  /** Índice en reproducción (iframe / video); null = preview con thumbnail. */
  playingIndex: number | null = null;
  youtubeConsent = false;

  private readonly autoplayDelay = 10000;
  private autoplayTimer: ReturnType<typeof setInterval> | null = null;
  private isAutoplayPaused = false;
  private readonly safeUrlCache = new Map<string, SafeResourceUrl>();
  private consentSub: Subscription | null = null;

  constructor(
    private readonly homeTestimoniosService: HomeTestimoniosService,
    private readonly sanitizer: DomSanitizer,
    private readonly cookieConsent: CookieConsentService
  ) {}

  get previousIndex(): number {
    if (!this.testimonials.length) {
      return 0;
    }
    return (
      (this.currentIndex - 1 + this.testimonials.length) %
      this.testimonials.length
    );
  }

  get nextIndex(): number {
    if (!this.testimonials.length) {
      return 0;
    }
    return (this.currentIndex + 1) % this.testimonials.length;
  }

  ngOnInit(): void {
    this.consentSub = this.cookieConsent.state$.subscribe(() => {
      this.youtubeConsent = this.cookieConsent.isProviderAccepted('youtube');
      if (!this.youtubeConsent) {
        this.stopPlaying();
      }
    });

    this.homeTestimoniosService.loadContent().subscribe((content) => {
      this.eyebrow = content.eyebrow;
      this.applyTitle(content.title);
      this.testimonials = content.items;
      this.currentIndex = 0;
      this.stopPlaying();
      this.restartAutoplay();
    });
  }

  ngOnDestroy(): void {
    this.consentSub?.unsubscribe();
    this.stopAutoplay();
  }

  prevTestimonial(): void {
    if (!this.testimonials.length) {
      return;
    }
    this.stopPlaying();
    this.currentIndex =
      (this.currentIndex - 1 + this.testimonials.length) %
      this.testimonials.length;
  }

  nextTestimonial(): void {
    if (!this.testimonials.length) {
      return;
    }
    this.stopPlaying();
    this.currentIndex =
      (this.currentIndex + 1) % this.testimonials.length;
  }

  goToTestimonial(index: number): void {
    if (index === this.currentIndex) {
      return;
    }
    this.stopPlaying();
    this.currentIndex = index;
  }

  getCardState(index: number): string {
    if (index === this.currentIndex) return 'active';
    if (index === this.previousIndex) return 'prev';
    if (index === this.nextIndex) return 'next';
    return 'hidden';
  }

  isPlaying(index: number): boolean {
    return this.playingIndex === index;
  }

  canPlay(item: HomeTestimonioItem): boolean {
    if (item.tipoBanner === 'VideoEncuesta') {
      return !!item.mediaUrl;
    }
    return !!item.embedUrl && this.youtubeConsent;
  }

  displayThumbnail(item: HomeTestimonioItem): string {
    if (item.tipoBanner === 'VideoEncuesta') {
      return item.thumbnailUrl;
    }
    return this.youtubeConsent ? item.thumbnailUrl : YOUTUBE_PLACEHOLDER_THUMB;
  }

  playTestimonial(index: number, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.youtubeConsent) {
      return;
    }
    const item = this.testimonials[index];
    if (!item || !this.canPlay(item)) {
      return;
    }
    if (index !== this.currentIndex) {
      this.currentIndex = index;
    }
    this.playingIndex = index;
    this.pauseAutoplay();
  }

  onMediaEnded(): void {
    this.stopPlaying();
    this.resumeAutoplay();
  }

  pauseAutoplay(): void {
    this.isAutoplayPaused = true;
  }

  resumeAutoplay(): void {
    if (this.playingIndex != null) {
      return;
    }
    this.isAutoplayPaused = false;
  }

  safeMediaUrl(url: string | null | undefined): SafeResourceUrl | null {
    if (!url?.trim()) {
      return null;
    }
    const key = url.trim();
    const cached = this.safeUrlCache.get(key);
    if (cached) {
      return cached;
    }
    const safe = this.sanitizer.bypassSecurityTrustResourceUrl(key); // NOSONAR — URL de API/admin
    this.safeUrlCache.set(key, safe);
    return safe;
  }

  embedUrlWithAutoplay(embedUrl: string): string {
    try {
      const u = new URL(embedUrl);
      u.searchParams.set('autoplay', '1');
      return u.toString();
    } catch {
      return embedUrl.includes('?')
        ? `${embedUrl}&autoplay=1`
        : `${embedUrl}?autoplay=1`;
    }
  }

  private stopPlaying(): void {
    this.playingIndex = null;
  }

  private applyTitle(title: string): void {
    const marker = 'emprendedores';
    const idx = title.toLowerCase().indexOf(marker);
    if (idx > 0) {
      this.titleLead = title.slice(0, idx).trimEnd();
      this.titleAccent = title.slice(idx).trim();
    } else {
      this.titleLead = title;
      this.titleAccent = '';
    }
  }

  private restartAutoplay(): void {
    this.stopAutoplay();
    this.isAutoplayPaused = false;
    this.startAutoplay();
  }

  private startAutoplay(): void {
    if (this.autoplayTimer || this.testimonials.length <= 1) {
      return;
    }

    this.autoplayTimer = setInterval(() => {
      if (!this.isAutoplayPaused && this.playingIndex == null) {
        this.nextTestimonial();
      }
    }, this.autoplayDelay);
  }

  private stopAutoplay(): void {
    if (!this.autoplayTimer) {
      return;
    }
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }
}
